/**
 * shared.js — Shared UI Components & Utility Functions
 *
 * Contains reusable components and helpers used across multiple pages:
 *   - getStatusBadge()  — Maps a status string to a color-coded badge element.
 *   - Modal             — Generic modal dialog (overlay + header + body + footer).
 *   - ConfirmModal      — Pre-built delete confirmation dialog using Modal.
 *   - EmptyState        — Placeholder shown when a data table has no rows.
 *   - formatCurrency()  — Formats a numeric amount as a localized currency string.
 *   - formatDate()      — Formats an ISO date string as "Jan 1, 2024".
 *   - formatDateTime()  — Formats an ISO date string as "Jan 1, 10:30 AM".
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { X, Paperclip, AlertTriangle, AlertOctagon, ChevronUp, ChevronDown, ChevronsUpDown, Copy, Check,
       } from 'lucide-react';
import { VEHICLE_GLYPHS, DEFAULT_VEHICLE_GLYPH } from './VehicleGlyphs';
import { formatTrackingOrderStatus, isRouteStatus, normalizeOrderStatus } from '../utils/orderStatus';
import { CATEGORICAL, DEFAULT_CATEGORY } from '../constants/palette';
import { normalizeVehicleType } from '../config/vehicleTypes';

/**
 * Returns a styled <span> badge for the given status string.
 *
 * The function normalises the status to lowercase, looks it up in a colour map,
 * and returns a pill-shaped badge with a colour-coded dot + label.
 *
 * @param {string} status - The status value (e.g., 'active', 'in_transit', 'maintenance').
 * @returns {JSX.Element|null} A <span> badge element, or null if status is falsy.
 */
/**
 * One frozen empty array for every `const { data: x = NONE } = useQuery(...)`.
 *
 * Writing `= []` there looks harmless, but while a query is loading `data` is
 * undefined, so the default builds a BRAND NEW array on every render. Any
 * useEffect or useMemo listing that value as a dependency then re-runs every
 * render — and if it sets state, the component loops until React gives up with
 * "Maximum update depth exceeded". That was happening on Orders, on every page
 * load. A module-level constant has a stable identity, so the loop cannot
 * start. Frozen so a caller cannot push into the shared instance.
 */
export const NONE = Object.freeze([]);

export function getStatusBadge(status) {
  if (!status) return null;
  const s = normalizeOrderStatus(status);

  /* Map of known statuses → CSS class names (defined in App.css).

     THE FIVE ORDER STAGES GET FIVE DISTINCT INKS. `scheduled` and `dispatched`
     both pointed at `badge-amber`, which meant a grid of orders drew the first
     two stages of the workflow in identical colour — you could not tell a load
     still sitting in the yard from one already released, which is the single
     most operationally important distinction on the page. They also disagreed
     with the status chips above them, which use the lane inks.
     One vocabulary now: chip, corridor chip and row badge all agree. */
  const map = {
    active: 'badge-green', running: 'badge-green', success: 'badge-green',
    inactive: 'badge-gray', offline: 'badge-gray',
    new: 'badge-amber',
    maintenance: 'badge-amber', pending: 'badge-amber',

    scheduled:  'badge-scheduled',
    dispatched: 'badge-dispatched',
    enroute:    'badge-enroute',
    en_route:   'badge-enroute', 'en route': 'badge-enroute',
    in_transit: 'badge-enroute', transit: 'badge-enroute', 'in transit': 'badge-enroute',
    completed:  'badge-delivered',
    cancelled:  'badge-void',

    error: 'badge-red', failed: 'badge-red', overdue: 'badge-red',
    refrigerated: 'badge-cyan', express: 'badge-purple'
  };

  const cls = map[s] || 'badge-gray';
  const label = isRouteStatus(s)
    ? formatTrackingOrderStatus(s)
    : String(status).replace(/_/g, ' ');
  return <span className={`badge ${cls}`}>{label}</span>;
}

/**
 * Modal — A generic modal dialog component.
 *
 * Renders a full-screen overlay with a centered card containing a header, body, and
 * optional footer. Clicking the overlay backdrop closes the modal.
 *
 * @param {string}   title    - Text shown in the modal header.
 * @param {function} onClose  - Callback fired when the modal should close.
 * @param {node}     children - Content rendered inside the modal body.
 * @param {node}     [footer] - Optional footer content (e.g., action buttons).
 * @param {string}   [size]   - 'md' (default) or 'sm' for a narrower confirmation dialog.
 */
export function Modal({ title, subtitle, icon: Icon, onClose, children, footer, size = 'md', disableBackdropClose = false, busy = false, bodyClass = '' }) {
  /* CALM POPUPS (Oct 2026). `subtitle` is one plain sentence under the title
     saying what this popup makes; `icon` is a lucide component drawn in a
     small tinted square beside it. Both optional: a popup without them looks
     exactly as before apart from the quieter header. */
  const titleId = React.useId();
  /* Keyboard focus moves into the popup when it opens (unless a field inside
     already took it) and goes back to whatever opened it when it closes, so
     Enter and Tab act on the popup, not on the page behind it. */
  const dialogRef = React.useRef(null);
  React.useEffect(() => {
    const opener = document.activeElement;
    const el = dialogRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => { if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true }); };
  }, []);
  // sm → confirm dialog, md → default form, wide → a form that needs room but
  // not a full page, lg → record/wizard.
  const sizeClass = size === 'sm' ? 'confirm-modal'
                  : size === 'wide' ? 'modal-wide'
                  : size === 'lg' ? 'modal-lg' : '';

  /* UNSAVED CHANGES (Round 4, Oct 2026). Once the user has typed, picked or
     ticked anything inside this modal, closing it by X, Escape, the backdrop
     or a Cancel button marked `data-modal-cancel` asks first. Only real user
     input counts: a form the page fills in programmatically after opening is
     not "changed". Saving closes through the page's own code, so it never
     asks. A search box (type="search" or data-no-dirty) does not count. */
  const touchedRef = React.useRef(false);
  const markTouched = React.useCallback((e) => {
    const t = e.target;
    if (!t || typeof t.matches !== 'function') return;
    if (t.matches('input[type="search"], [data-no-dirty], [data-no-dirty] *')) return;
    if (t.matches('input, textarea, select, [contenteditable="true"]')) touchedRef.current = true;
  }, []);
  const okToClose = () => !touchedRef.current
    || window.confirm('You have changes that are not saved. Close and lose them?');
  const guardedClose = React.useCallback(() => { if (okToClose()) onClose(); }, [onClose]); // eslint-disable-line react-hooks/exhaustive-deps
  const guardCancel = (e) => {
    const el = e.target && e.target.closest ? e.target.closest('[data-modal-cancel]') : null;
    if (!el || el.disabled) return;
    if (!okToClose()) { e.stopPropagation(); e.preventDefault(); }
  };

  // Escape closes the modal — standard dialog ergonomics. Skipped when
  // backdrop-close is disabled (flows that must not be dismissed mid-save).
  React.useEffect(() => {
    if (disableBackdropClose || busy) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') guardedClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [guardedClose, disableBackdropClose, busy]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="modal-overlay" onClick={e => !disableBackdropClose && !busy && e.target === e.currentTarget && guardedClose()}>
      <div className={`modal ${sizeClass}`} onClick={(e) => e.stopPropagation()} ref={dialogRef} tabIndex={-1}
           role="dialog" aria-modal="true" aria-labelledby={titleId}
           onInputCapture={markTouched} onChangeCapture={markTouched}>
        {/* Header with title and close button */}
        {/* `busy`, deliberately NOT `disableBackdropClose`. Most callers set
            that one permanently, to stop a stray backdrop click discarding a
            long form — tying the X to it would leave those modals with no
            close button at all. `busy` means a request is in flight.

            Without it the X was the one way out of a flow that had blocked
            the other two: pressing it mid-save hid the modal without
            cancelling the request, so the record still saved while the user
            believed they had cancelled — and when the request resolved, its
            `onClose` shut whatever the user had opened since, discarding a
            half-typed form under a toast about a different record. */}
        <div className={`modal-header ${subtitle || Icon ? 'has-sub' : ''}`}>
          {Icon && <span className="modal-glyph" aria-hidden="true"><Icon size={19} strokeWidth={1.8} /></span>}
          <div className="modal-titles">
            <span className="modal-title" id={titleId}>{title}</span>
            {subtitle && <p className="modal-sub">{subtitle}</p>}
          </div>
          <button className="modal-x" type="button" title="Close" aria-label="Close"
                  disabled={busy} onClick={guardedClose}><X size={16} /></button>
        </div>
        {/* Main content area */}
        {/* bodyClass lets a caller opt out of the standard padding — the
            vehicle wizard runs a rail flush to the modal edge, which the
            default 22px inset would break. Everything else ignores it. */}
        <div className={`modal-body ${bodyClass}`}>{children}</div>
        {/* Footer with action buttons (rendered only if provided) */}
        {footer && <div className="modal-footer" onClickCapture={guardCancel}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

/**
 * ConfirmModal — A specialized confirmation dialog for delete actions.
 *
 * Wraps the Modal component with a small size, "Confirm Delete" title, and
 * Cancel / Delete buttons.
 *
 * @param {string}   message   - The confirmation message shown to the user.
 * @param {function} onConfirm - Callback fired when the user clicks "Delete".
 * @param {function} onCancel  - Callback fired when the user clicks "Cancel" or closes.
 * @param {{ message: string, items?: string[] }} [error] - Why the last attempt
 *   was refused, shown under the message so it stays next to the button the
 *   user pressed. `items` lists the records in the way (fleets, vehicles).
 */
export function ConfirmModal({ message, onConfirm, onCancel, title = 'Confirm Delete', confirmLabel = 'Delete', confirmClass = 'btn-danger', busy = false, busyLabel = 'Working…', error = null }) {
  /* `busy` exists because the dialog used to stay open and enabled for the
     whole request: a second click fired a second delete, the first succeeded
     and the second 404'd, so one order produced both a success and a failure
     toast. Optional, so every existing caller is unaffected.

     `error` exists because Zone/delete and Fleet/delete now refuse with a 409
     that names the fleets or vehicles still attached. The dialog already
     stayed open on failure — only the success path closes it — but the reason
     went out as a toast that scrolled away behind the modal. Also optional. */
  return (
    <Modal title={title} onClose={busy ? () => {} : onCancel} size="sm" footer={
      <>
        <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className={`btn ${confirmClass}`} onClick={onConfirm} disabled={busy}>{busy ? busyLabel : confirmLabel}</button>
      </>
    }>
      <p className="confirm-message">{message}</p>
      {error?.message && (
        <div className="confirm-error" role="alert">
          <p className="confirm-error-msg">{error.message}</p>
          {Array.isArray(error.items) && error.items.length > 0 && (
            <ul className="confirm-error-list">
              {error.items.map((label, i) => <li key={i}>{label}</li>)}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * EmptyState — Placeholder displayed when a data table has no matching records.
 *
 * Shows a large faded icon and a descriptive message.
 *
 * @param {React.ComponentType} icon    - A Lucide icon component to render.
 * @param {string}              message - Descriptive text (e.g., "No vehicles found").
 */
/**
 * Shown when a list has no rows.
 *
 * Deliberately NOT the big-faded-centred-icon-in-a-void pattern: that layout
 * is the single most recognisable stock-template screen there is, and it
 * wastes the whole viewport to say one short sentence. This reads as a ruled
 * note at the top of an empty ledger page instead — small icon inline with
 * the text, left-aligned on the same grid as the table that would be here,
 * with the next action sitting right next to it.
 *
 * `icon` is still accepted so every existing call site keeps working.
 */
/**
 * Grid select options, widened by whatever the data actually holds.
 *
 * Most of these lists are hand-written constants, but almost none of the
 * fields behind them are closed enums: `mapContactFromApi`, `mapVendorFromApi`,
 * `mapFleetFromApi` and friends all resolve status as `item.status || 'active'`
 * — free text, both directions. So the server can and does hand back values the
 * dropdown never listed. The demo roster has a driver "On leave"; the type
 * filters on Contacts and Places are already built from the real data for
 * exactly this reason.
 *
 * A dropdown that cannot express a value the record already holds is a trap
 * rather than a constraint: the user can change that row to something else and
 * never change it back, and a bulk set over a range containing one overwrites
 * it with no route home. (A single-cell edit is already safe — `CellEditor`
 * injects a "— not on the list" option — but the bulk bar renders `col.options`
 * and nothing else.)
 *
 * So: the app's own list first, in its own order, then anything else the loaded
 * rows actually use, appended. `labelOf` formats the extras; by default they
 * are shown verbatim, which is the honest thing to do with a value we have no
 * translation for.
 */
export function optionsWithPresent(options, rows, key, labelOf = (v) => String(v)) {
  /* Compared the way the rest of the grid compares: as a trimmed, lower-cased
     string. `parseValue`, `displayValue` and `CellEditor` all match options
     with `String(...)`, and the mappers store these fields as free text — so a
     server row reading "Active" against an option of "active" is entirely
     possible. Matching by strict identity produced TWO entries both labelled
     "Active", and picking the wrong one wrote a value the status inks and
     badges don't recognise. Numbers normalise the same way, so an option of
     `1` is not duplicated by a row carrying `"1"`. */
  const norm = (v) => String(v).trim().toLowerCase();
  const known = new Set(options.map(o => norm(o && typeof o === 'object' ? o.value : o)));
  const seen = new Set();
  const extra = [];
  (Array.isArray(rows) ? rows : []).forEach(r => {
    const v = r?.[key];
    if (v == null || v === '') return;
    const n = norm(v);
    if (known.has(n) || seen.has(n)) return;
    seen.add(n);
    extra.push(v);
  });
  // Locale-aware and numeric, so 2 sorts before 10 rather than after it.
  extra.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  /* Two constraints, and the old single line satisfied only one of them.

     SHAPE: options are always returned in one shape, even if `options` came in
     as bare strings — every consumer reads `.value` / `.label`. The no-extras
     branch used to return the raw `options` ARGUMENT rather than the mapped
     `base`, contradicting the line above it: a caller passing
     `['active', 'inactive']` got bare strings back while no row carried an
     unknown value, and objects back the moment one did. Two different return
     types from the same call, decided by the data.

     IDENTITY: returning the caller's own array was also deliberate. Several
     callers hold this result inside a memo, so handing back a freshly-mapped
     copy on every render would invalidate those memos for no reason.

     Both hold at once: give back the caller's array when it is ALREADY all
     objects and there is nothing to append, and the normalised copy in every
     other case. */
  const allObjects = options.every(o => o && typeof o === 'object');
  if (allObjects && !extra.length) return options;
  const base = options.map(o => (o && typeof o === 'object' ? o : { value: o, label: String(o) }));
  return extra.length ? [...base, ...extra.map(value => ({ value, label: labelOf(value) }))] : base;
}

/**
 * ONE CELL THAT NEVER TELLS THE USER SOMETHING UNTRUE.
 *
 * Every grid on this app resolves a foreign key against a list fetched by a
 * separate query, and every one of them used to collapse the two failure modes
 * into one word:
 *
 *   const d = driverById.get(String(v.driverId));
 *   return d ? d.name : <span className="cell-muted">Unassigned</span>;
 *
 * "Unassigned" is a claim about the DATA — this vehicle has nobody driving it.
 * But the same branch fires when `driverId` is a perfectly good 47 and the
 * drivers query simply hasn't come back yet, or came back empty because it
 * failed, or the driver was deleted while the vehicle still points at them.
 * Three different situations, one confident sentence, and the only one it
 * describes correctly is the first. An operator reading a screen of
 * "Unassigned" during a slow load would go and assign drivers to vehicles that
 * already had them.
 *
 * The fix is to say only what the row actually knows:
 *   no id            → "Unassigned" — genuinely empty, and a fact.
 *   id + record      → the record.
 *   id, no record    → "Driver #47 (not found)" — names the id so it can be
 *                      chased, and never claims the seat is empty.
 *
 * `children`, if given, is a render function for the found record, so a caller
 * that wants a colour dot or a tag keeps it.
 */
export function RefName({ id, record, noun = 'Record', emptyText = 'Unassigned', children }) {
  if (id == null || id === '') return <span className="cell-muted">{emptyText}</span>;
  if (record) return children ? children(record) : (record.name || `${noun} #${id}`);
  return (
    <span className="cell-muted" title={`This row points at ${noun.toLowerCase()} ${id}, which is not in the loaded list — it may still be loading, or it may have been deleted.`}>
      {`${noun} #${id} (not found)`}
    </span>
  );
}

export function EmptyState({ icon: Icon, message, actionLabel, onAction }) {
  return (
    <div className="empty-state">
      <div className="empty-state-row">
        {Icon && <Icon size={16} strokeWidth={1.75} className="empty-state-icon" />}
        <p>{message}</p>
      </div>
      {/* An empty list should say what to do about it, not just that it's empty. */}
      {actionLabel && onAction && (
        <button className="btn btn-secondary btn-sm" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Checkbox / radio grid for picking a user's scope — the fleets or zones a
 * role is attached to.
 *
 * Both the create-user and edit-user forms on the Security page need this for
 * fleets and for zones, which had it copy-pasted four times over. `multi`
 * decides checkboxes vs single-pick radios: a Fleet Manager may hold several
 * fleets and an Org Admin or Zone Manager several zones, while a Driver or
 * Operator was never meant to hold more than one.
 *
 * @param {Array<{id: number, name: string}>} items   Everything selectable.
 * @param {Array<number>} selectedIds                 Currently ticked ids.
 * @param {boolean} multi                             Checkboxes when true, radios when false.
 * @param {string} name                               Radio group name — must be unique per grid.
 * @param {(id: number) => void} onToggle             Called with the clicked id.
 * @param {string} emptyText                          Shown instead of the grid when `items` is empty.
 * @param {string} label                              Names the group for screen readers. The
 *                                                    visible <label> above the grid is attached to
 *                                                    no control, so without this the options are
 *                                                    announced with nothing saying whether they
 *                                                    are fleets or zones.
 */
export function ScopePicker({ items, selectedIds = [], multi, name, onToggle, emptyText, label }) {
  if (!items || items.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{emptyText}</div>;
  }
  return (
    <div role={multi ? 'group' : 'radiogroup'} aria-label={label} style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: 8,
      padding: '10px 12px',
      border: '1px solid var(--border)',
      borderRadius: 8,
    }}>
      {items.map(item => {
        const checked = selectedIds.includes(item.id);
        return (
          <label
            key={item.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontWeight: 400, fontSize: 13,
              padding: '4px 6px', borderRadius: 4,
              background: checked ? 'var(--accent-glow)' : 'transparent',
            }}
          >
            <input
              type={multi ? 'checkbox' : 'radio'}
              name={name}
              checked={checked}
              onChange={() => onToggle(item.id)}
              style={{ width: 14, height: 14 }}
            />
            {checked && multi && <Check size={12} style={{ color: 'var(--text-accent)', flexShrink: 0 }} />}
            {item.name}
          </label>
        );
      })}
    </div>
  );
}

/**
 * Small inline flag for an expiry date that has passed or is close.
 *
 * Consolidates a block that was copy-pasted into Drivers, Vehicles and
 * VehiclesTracker — each with a `●` or `⚠` text glyph standing in for an
 * icon. Typographic glyphs don't inherit icon sizing or optical alignment,
 * and `●`/`⚠` render differently per platform font, so they're replaced with
 * real Lucide icons here.
 *
 * @param {'expired'|'expiring'|string} state
 * @param {string} label  Already-translated label text.
 */
export function ExpiryFlag({ state, label }) {
  if (state !== 'expired' && state !== 'expiring') return null;
  const Icon = state === 'expired' ? AlertOctagon : AlertTriangle;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        marginLeft: 6, fontSize: 10, whiteSpace: 'nowrap',
        color: state === 'expired' ? 'var(--danger)' : 'var(--warning)',
      }}
    >
      <Icon size={11} strokeWidth={2} />
      {label}
    </span>
  );
}

/**
 * Shown when a fetch fails. Unlike EmptyState this keeps a centred, larger
 * treatment: an empty list is a normal condition and should stay out of the
 * way, but a failed load is a genuine stop and should read as one. It has its
 * own class rather than sharing `.empty-state`, whose layout is now a
 * horizontal row.
 */
export function ErrorState({ message = 'Failed to load data.', onRetry }) {
  return (
    <div className="error-state">
      <AlertTriangle size={22} strokeWidth={1.75} style={{ color: 'var(--danger)' }} />
      <p>{message}</p>
      {onRetry && (
        <button className="btn btn-secondary btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Returns the system-wide base currency code from localStorage settings.
 * Falls back to 'NGN' if not configured.
 *
 * @returns {string} ISO 4217 currency code.
 */
export function getSystemBaseCurrency() {
  try {
    const saved = localStorage.getItem('fleet_currency_settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.baseCurrency) return parsed.baseCurrency;
    }
  } catch (_) {}
  return 'NGN';
}

/**
 * Formats a numeric amount as a localized currency string.
 *
 * @param {number} amount   - The monetary value to format.
 * @param {string} [currency] - ISO 4217 currency code. Defaults to the system base currency.
 * @returns {string} Formatted string (e.g., "$1,200.00") or "—" if amount is null/undefined.
 */
export function formatCurrency(amount, currency = getSystemBaseCurrency()) {
  if (amount == null) return '—';
  /* A DEFAULT PARAMETER ONLY FIRES ON `undefined`.
     Every call of the shape formatCurrency(x, row.currency) where the row
     carries null or '' — which is most of them, since the mappers coerce a
     missing currency to '' — reached Intl with `currency: null` and threw
     `RangeError: Invalid currency code`. There is no error boundary on these
     pages, so that is a blank screen, not a bad cell. `'?'` gets the same
     treatment: totalByCurrency emits it for rows of unknown denomination and
     it is not a code Intl accepts either. */
  let code = typeof currency === 'string' ? currency.trim().toUpperCase() : '';
  if (!/^[A-Z]{3}$/.test(code)) code = getSystemBaseCurrency();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
  } catch {
    // An unknown but well-formed code (a retired ISO code, a typo in settings)
    // still reaches Intl. Print the number and the code rather than crashing.
    return `${code} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/**
 * MONEY THAT CROSSES CURRENCIES — the one place the reports convert.
 *
 * THE BUG THIS EXISTS TO KILL. Every report used to total money like this:
 *
 *     const revenue = orders.reduce((s, o) => s + (o.totalAmount || 0), 0);
 *     money(revenue);   // formatted in the org's base currency
 *
 * A `totalAmount` is meaningless without its `currency`, and this org trades in
 * three. That line added $214,764.65 to €88,414.05, got 303,178.70, and printed
 * it as €303,178.70 — a number that is not the total in euros, not the total in
 * dollars, and not the total in anything. It was the General Report's headline.
 *
 * Ranked lists were worse, because a ranking is a comparison and each bar was a
 * different arbitrary mixture. On the live data, "Revenue earned per vehicle"
 * put Hamra Hopper first at €6,303 — actually $3,494 + ₦2,809, about €3,207,
 * which is third. Four of the five positions were wrong. The chart wasn't
 * mislabelled; it was ranking the fleet in nearly reverse order.
 *
 * The rates were sitting in Settings the whole time.
 *
 * `rateToUsd` is "multiply by this to get USD", so converting between any two
 * currencies goes via USD: base = (amount × rate[from]) / rate[base].
 *
 * WHAT IT WILL NOT DO. If a currency has no usable rate, `toBase` returns null
 * rather than a number — a missing rate must not silently become a 1:1
 * conversion, which would turn ₦2,809 into €2,809. `sumToBase` collects those
 * separately in `unconverted` so the caller can say so out loud instead of
 * quietly dropping or mangling them.
 */
export function createMoney(currencySettings) {
  const base = currencySettings?.baseCurrency || getSystemBaseCurrency();
  const rates = {};
  (currencySettings?.currencies || []).forEach((c) => {
    const r = Number(c?.rateToUsd);
    if (c?.code && Number.isFinite(r) && r > 0) rates[c.code] = r;
  });
  // USD is the pivot and its rate to itself is 1 by definition, so a settings
  // list that simply omits USD still converts.
  if (!rates.USD) rates.USD = 1;

  const toBase = (amount, code) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    const from = code || base;
    if (from === base) return n;
    if (!rates[from] || !rates[base]) return null;
    return (n * rates[from]) / rates[base];
  };

  /**
   * Total a list in the base currency, keeping the honest per-currency split.
   * @returns {{ base:number, byCurrency:Map, currencies:string[],
   *             unconverted:string[], mixed:boolean }}
   */
  const sumToBase = (list, amountOf = (x) => x.amount, currencyOf = (x) => x.currency) => {
    const byCurrency = new Map();
    const unconverted = new Set();
    let total = 0;
    (Array.isArray(list) ? list : []).forEach((row) => {
      const amt = Number(amountOf(row)) || 0;
      if (!amt) return;
      const code = currencyOf(row) || base;
      byCurrency.set(code, (byCurrency.get(code) || 0) + amt);
      const conv = toBase(amt, code);
      if (conv == null) unconverted.add(code);
      else total += conv;
    });
    const currencies = [...byCurrency.keys()];
    return {
      base: total,
      byCurrency,
      currencies,
      unconverted: [...unconverted],
      // `mixed` drives the "converted" note: one currency that IS the base
      // needs no disclosure, anything else does.
      mixed: currencies.length > 1 || (currencies.length === 1 && currencies[0] !== base),
    };
  };

  return {
    base,
    rates,
    toBase,
    sumToBase,
    /** Format in the base currency. */
    format: (v) => formatCurrency(v, base),
    /** Format in a specific currency, falling back to base. */
    formatIn: (v, code) => formatCurrency(v, code || base),
    /** "$214,764.65 + €88,414.05" — the split, in full, for a note or tooltip. */
    describe: (totals) => (totals?.currencies || [])
      .map((c) => formatCurrency(totals.byCurrency.get(c), c))
      .join(' + '),
  };
}

/**
 * The one-line disclosure that has to sit next to any converted figure.
 * A converted total reads as money actually received unless it says otherwise,
 * and the rates move — so the note names the split it came from.
 */
export function ConvertedNote({ totals, money, className = '' }) {
  if (!totals?.mixed) return null;
  return (
    <span className={`money-conv ${className}`.trim()}>
      converted · {money.describe(totals)}
      {totals.unconverted.length > 0 && (
        <b> · no rate for {totals.unconverted.join(', ')}</b>
      )}
    </span>
  );
}

/**
 * Formats an ISO date string into a short human-readable date.
 *
 * @param {string} dateStr - ISO 8601 date string (e.g., "2024-01-15").
 * @returns {string} Formatted date (e.g., "Jan 15, 2024") or "—" if empty.
 */
export function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Formats an ISO date string into a short date + time string.
 *
 * @param {string} dateStr - ISO 8601 datetime string (e.g., "2024-01-15T08:00:00Z").
 * @returns {string} Formatted datetime (e.g., "Jan 15, 08:00 AM") or "—" if empty.
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Document Avatar ────────────────────────────────────────────────────────────
// In-memory cache: documentId → base64 data URL (avoids re-fetching per render)
const _docCache = {};

/**
 * Shows a circular avatar fetched from the StoredDocuments API.
 * Falls back to an SVG placeholder ('person' or 'vehicle') if no document.
 */
/**
 * Silhouettes live in VehicleGlyphs.js — one drawn per canonical type, because
 * the icon library only had four vehicle shapes for six types and no flatbed.
 * Legacy "Pickup" is normalised to "Pick Up" before lookup, so rows saved
 * under the old spelling still resolve.
 */

export function DocumentAvatar({ documentId, fallback = 'person', api, token, size = 36, style = {},
                                 vehicleType = null, identityName = null }) {
  const [src, setSrc] = React.useState(documentId ? (_docCache[documentId] || null) : null);

  /* `src` was only ever SET, never cleared — so when documentId went to null
     (a record with no photo) or changed to one whose fetch failed, the previous
     record's photograph stayed on screen as if it belonged to this one. And
     with no cancellation, two overlapping fetches resolved in arbitrary order,
     so the wrong image could stick. Clear first, then fetch, and ignore any
     response that arrives after the id has moved on. */
  React.useEffect(() => {
    if (!documentId || !api || !token) { setSrc(null); return undefined; }
    const cached = _docCache[documentId];
    if (cached) { setSrc(cached); return undefined; }
    let alive = true;
    setSrc(null);
    api.documents.getById(documentId, token)
      .then(doc => {
        if (!alive) return;
        if (doc?.contentBase64 && doc?.contentType) {
          const dataUrl = `data:${doc.contentType};base64,${doc.contentBase64}`;
          _docCache[documentId] = dataUrl;
          setSrc(dataUrl);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [documentId, api, token]);

  const isVehicle = fallback === 'vehicle';
  const isPackage = fallback === 'package';
  // Ledger radius vocabulary: one small fixed radius, not a size-scaled blob.
  const radius = 4;
  const boxStyle = {
    width: size, height: size,
    borderRadius: radius,
    overflow: 'hidden', flexShrink: 0,
    background: 'var(--bg-hover)',
    border: '1.5px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    ...style,
  };

  if (src) {
    return (
      <div style={boxStyle}>
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  if (isVehicle) {
    // The old fallback was one stock photo of a white van repeated on every
    // row — identical for a tanker, a pickup and a refrigerated truck, so it
    // occupied the most prominent cell in the table while carrying no
    // information at all. This carries two things instead: the silhouette
    // says what kind of vehicle it is, and the tint is derived from its name
    // so the same vehicle keeps the same colour everywhere it appears.
    const Icon  = VEHICLE_GLYPHS[normalizeVehicleType(vehicleType)] || DEFAULT_VEHICLE_GLYPH;
    const c     = identityColor(identityName);
    return (
      <div
        className="vehicle-avatar"
        style={{
          ...boxStyle,
          background: `${c.pin}14`,
          borderColor: `${c.pin}33`,
        }}
        title={vehicleType || 'Vehicle'}
      >
        <Icon
          size={Math.round(size * 0.46)}
          strokeWidth={1.6}
          color={c.text}
          className="vehicle-avatar-glyph"
        />
      </div>
    );
  }

  if (isPackage) {
    return (
      <div style={{ ...boxStyle, background: 'var(--bg-elevated)' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none"
          stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      <img src="/profile-pic-placeholder.png" alt="person"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </div>
  );
}

/**
 * SortableTh — clickable table header wired to useTableSort.
 * Inactive columns show a faint both-ways glyph so sortability is
 * discoverable without shouting; the active column shows direction.
 */
export function SortableTh({ col, sort, children, className = '', title }) {
  const active = sort.sortCol === col;
  return (
    /* These were click-only: a mouse could sort every table in the app and a
       keyboard could sort none of them. */
    <th
      className={`th-sort ${className}`.trim()}
      onClick={() => sort.toggle(col)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sort.toggle(col); }
      }}
      tabIndex={0}
      title={title}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      aria-sort={active ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {children}
        {active
          ? (sort.sortDir === 'asc'
              ? <ChevronUp size={12} style={{ flexShrink: 0 }} />
              : <ChevronDown size={12} style={{ flexShrink: 0 }} />)
          : <ChevronsUpDown size={12} style={{ opacity: 0.3, flexShrink: 0 }} />}
      </span>
    </th>
  );
}

// Small integer hash so the same name always lands on the same palette slot —
// stable across renders and across every table the name appears in.
function _nameHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * The palette slot a given name always lands on. Exported so anything that
 * needs to tint by identity — avatars, initials chips, the vehicle preview in
 * the Add form — resolves the same colour from the same place instead of each
 * caller re-deriving it and quietly drifting apart.
 *
 * @param {string|null} name
 * @returns {{pin: string, text: string}} pin for fills, text for type
 */
export function identityColor(name) {
  if (!name) return DEFAULT_CATEGORY;
  return CATEGORICAL[_nameHash(name) % CATEGORICAL.length];
}

/**
 * IdentityTag — initials chip + name, for Driver/Customer columns in dense
 * tables (Orders, Dashboard's Recent Orders, etc.).
 *
 * Every well-scoped colour review of this app so far has landed on the same
 * rule: chrome (nav, headers, table heads) gets exactly one colour reserved
 * for state and action, because that's what keeps a 40-item menu or a
 * multi-page table calm. But identity is a different problem — across a
 * table of orders, the eye needs to tell "same driver" from "different
 * driver" at a glance, and the enterprise-table pattern for that (GitHub,
 * Linear, Attio) is a small colour-coded initials chip next to the name.
 *
 * The colour comes from a hash of the name, not the brand palette, so it
 * can never be mistaken for a status or an action — a given driver is always
 * the same colour, but that colour carries no meaning beyond "this one
 * again." Uses the same muted CATEGORICAL set as chart series and place
 * types, so it sits with the rest of the app's non-brand colour rather than
 * introducing a new ramp.
 */
export function IdentityTag({ name, size = 20 }) {
  if (!name) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const initials = name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const c = identityColor(name);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
      <span className="idtag-mark" style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        display: 'grid', placeItems: 'center',
        fontSize: Math.round(size * 0.42), fontWeight: 600, lineHeight: 1,
        background: `${c.pin}1c`, color: c.text, '--mono': c.pin,
      }}>{initials}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </span>
  );
}

/**
 * DetailRow — Labeled key/value row used in entity detail views.
 * Extracted from page files to avoid duplication across Vehicles, Drivers, etc.
 */
export function DetailRow({ label, value, mono = false }) {
  // Was styled inline, including a raw `monospace` that resolved to whatever
  // the OS supplies rather than the app's IBM Plex Mono — so plate numbers in
  // a detail view didn't match plate numbers in the table beside it.
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className={`detail-value ${mono ? 'is-mono' : ''}`}>
        {value || <span className="cell-muted">—</span>}
      </span>
    </div>
  );
}

/**
 * CopyableValue — a value with a copy-to-clipboard affordance.
 *
 * For values someone actually needs to paste elsewhere: a tracking number into
 * a carrier lookup, a signup code into an email to a customer. Removes the
 * "select the text precisely" friction.
 *
 * Module-level on purpose. This used to be declared inside a page's render,
 * which remounted it on every parent render and reset the "copied" tick
 * mid-animation; the timer also had no cleanup and could setState into an
 * unmounted component.
 */
export function CopyableValue({ value, title = 'Copy', mono = false, emptyText = '—' }) {
  // 'idle' | 'copied' | 'failed' — a tick must mean the clipboard actually took
  // the value. writeText rejects on a non-secure origin or denied permission,
  // and the API is absent entirely in some contexts; showing success anyway
  // would tell someone a signup code is on their clipboard when it is not.
  const [state, setState] = React.useState('idle');

  React.useEffect(() => {
    if (state === 'idle') return undefined;
    const timer = setTimeout(() => setState('idle'), state === 'failed' ? 2500 : 1200);
    return () => clearTimeout(timer);
  }, [state]);

  if (!value) return <>{emptyText}</>;

  const copy = (e) => {
    e.stopPropagation();
    Promise.resolve(navigator.clipboard?.writeText(String(value)) ?? Promise.reject(new Error('unavailable')))
      .then(() => setState('copied'))
      .catch(() => setState('failed'));
  };

  const color = state === 'copied' ? 'var(--success)'
              : state === 'failed' ? 'var(--danger)'
              : 'var(--text-muted)';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={mono ? { fontFamily: 'var(--font-mono)', letterSpacing: '0.06em' } : undefined}>
        {value}
      </span>
      <button
        type="button"
        title={state === 'failed' ? 'Copy failed — select the text and copy manually' : title}
        onClick={copy}
        style={{
          display: 'inline-flex', background: 'none', border: 'none',
          padding: 2, cursor: 'pointer', color,
        }}
      >
        {state === 'copied' ? <Check size={12} />
          : state === 'failed' ? <AlertTriangle size={12} />
          : <Copy size={12} />}
      </button>
    </span>
  );
}

/**
 * ProgressRing — donut showing how many required documents are uploaded.
 *
 * Grey while nothing is uploaded, amber part-way, green once complete, so the
 * state reads from the colour before the numbers are parsed.
 *
 * Module-level on purpose: this was declared inside the render bodies of both
 * Vehicles and Drivers, which meant React saw a brand-new component type on
 * every render and remounted the subtree. Harmless while it stays stateless,
 * but it would silently reset any state or animation added later — the same
 * bug CopyableValue had before it was extracted.
 */
export function ProgressRing({ uploaded, total }) {
  const r = 19, circ = 2 * Math.PI * r;
  const pct = total > 0 ? uploaded / total : 0;
  const color = pct === 0 ? 'var(--border)' : pct >= 1 ? '#5c7a3f' : 'var(--accent)';
  return (
    <div style={{ position: 'relative', width: 50, height: 50, flexShrink: 0 }}>
      <svg width="50" height="50" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="25" cy="25" r={r} fill="none" stroke="var(--border)" strokeWidth="3.5" />
        <circle cx="25" cy="25" r={r} fill="none" stroke={color} strokeWidth="3.5"
          strokeDasharray={circ} strokeDashoffset={pct > 0 ? circ * (1 - pct) : circ}
          strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-primary)' }}>
        {uploaded}/{total}
      </div>
    </div>
  );
}

/**
 * DocThumbnail — Fetches and renders a document by ID.
 * Shows an image preview for images, or a download button for other file types.
 */
export function DocThumbnail({ documentId, name, api, token }) {
  const [doc, setDoc] = React.useState(null);
  React.useEffect(() => {
    if (!documentId) return;
    api.documents.getById(documentId, token).then(setDoc).catch(() => {});
  }, [documentId, api, token]);

  if (!doc) return (
    <div style={{
      width: '100%', height: 80, borderRadius: 8,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)', fontSize: 12,
    }}>{name ? `Loading ${name}…` : 'Loading…'}</div>
  );

  const src = `data:${doc.contentType};base64,${doc.contentBase64}`;
  if (doc.contentType?.startsWith('image/')) {
    return (
      <img src={src} alt={name || `doc-${documentId}`}
        style={{ width: '100%', borderRadius: 8, objectFit: 'cover', maxHeight: 160, display: 'block' }} />
    );
  }

  const handleDownload = () => {
    const bytes = atob(doc.contentBase64);
    const arr = new Uint8Array(bytes.length).map((_, i) => bytes.charCodeAt(i));
    const blob = new Blob([arr], { type: doc.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name || `document-${documentId}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 8,
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      fontSize: 13,
    }}>
      <Paperclip size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{name || `Document ${documentId}`}</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={handleDownload}>Download</button>
    </div>
  );
}
